import axios from "axios";
import join from "url-join";
import { domain, ENDPOINT } from "../constants/api";
import {
  NETWORK_CONNECTION_MESSAGE,
  NOT_CONNECT_NETWORK_CODE,
} from "../constants/error";

const isAbsoluteURLRegex = /^(?:\w+:)\/\//;

axios.interceptors.request.use(async (config) => {
  try {
    if (!isAbsoluteURLRegex.test(config.url)) {
      config.url = join(domain, config.url);
    }
    config.timeout = 10 * 1000;
    return config;
  } catch (error) {
    return Promise.reject(error);
  }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("branchId");
  localStorage.removeItem("email");
  localStorage.removeItem("branchPOSId");

  const businessSlug = localStorage.getItem("businessSlug");

  window.location.href = `/${businessSlug}/login`;
};

const handleBusinessNotFound = () => {
  const event = new CustomEvent("businessNotFound");
  window.dispatchEvent(event);
  return Promise.reject({ message: "Business not found" });
};

const handleBusinessNotMatching = () => {
  const event = new CustomEvent("businessNotMatching");
  window.dispatchEvent(event);
  return Promise.reject({ message: "Business not matching" });
};

const handle401Error = async (originalRequest) => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    redirectToLogin();
    return Promise.reject({ message: "Refresh token is null" });
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      originalRequest.headers["Authorization"] = `Bearer ${token}`;
      return http(originalRequest);
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  return new Promise((resolve, reject) => {
    axios
      .post(`${domain}${ENDPOINT.USER_BUSINESS_GROUP}/verify-refresh-token`, {
        refresh_token: refreshToken,
      })
      .then(({ data }) => {
        const { access_token, refresh_token } = data.data;
        localStorage.setItem("accessToken", access_token);
        localStorage.setItem("refreshToken", refresh_token);
        http.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${access_token}`;
        originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
        processQueue(null, access_token);
        resolve(http(originalRequest));
      })
      .catch((err) => {
        redirectToLogin();
        processQueue(err, null);
        reject(err);
      })
      .finally(() => {
        isRefreshing = false;
      });
  });
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject({ isCancelled: true });
    }

    const originalRequest = error.config;

    if (
      error?.response?.status === 403 &&
      error.response?.data?.message === "BUSINESS_NOT_MATCHING"
    ) {
      return handleBusinessNotMatching();
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      return handle401Error(originalRequest);
    }

    if (
      error?.response?.status === 404 &&
      error.response?.data?.error === "BUSINESS_NOT_FOUND"
    ) {
      return handleBusinessNotFound();
    }

    if (!error.response) {
      return Promise.reject({
        code: NOT_CONNECT_NETWORK_CODE,
        message: NETWORK_CONNECTION_MESSAGE,
      });
    }

    return Promise.reject({
      code: error.response.status,
      message:
        error.response.data.error ||
        error.response.data.message ||
        "An error occurred",
    });
  }
);

export const http = axios;
